'use client';


import { useMemo, useState } from 'react';
import { calculateSpeakerPlacement, type SpeakerPlacementResult } from '@/lib/acousticsPhysics';
import { resizeRoom, type RoomBounds } from '@/lib/tools/roomLayout';
import type { LengthUnit } from '@/lib/tools/units';
import { formatLength } from '@/lib/tools/units';
import UnitToggle from '@/components/tools/UnitToggle';
import ToolDimSlider from '@/components/tools/ToolDimSlider';
import ToolMetricCard from '@/components/tools/ToolMetricCard';
import ToolLeadIn from '@/components/tools/ToolLeadIn';
import RoomPlan2D, { type PlanDimLine } from '@/components/tools/RoomPlan2D';

/**
 * Single source of truth for this widget's resize ceiling. Sliders derive
 * their min/max from this object (rather than duplicating the numbers) so
 * the wall-handle drag path (RoomPlan2D -> resizeRoom) and the slider path
 * can never drift apart — see SEO_AND_TOOLS.md gotcha #3.
 */
const PLAN_BOUNDS: RoomBounds = {
  length: { min: 2.5, max: 12 },
  width: { min: 2.2, max: 10 },
};

interface WidgetProps {
  initialLengthM: number;
  initialWidthM: number;
  initialPlacement: SpeakerPlacementResult;
}

export default function SpeakerPlacementWidget({
  initialLengthM,
  initialWidthM,
}: WidgetProps) {
  const [unit, setUnit] = useState<LengthUnit>('m');
  const [lengthM, setLengthM] = useState(initialLengthM);
  const [widthM, setWidthM] = useState(initialWidthM);

  const placement = useMemo(() => calculateSpeakerPlacement(lengthM, widthM), [lengthM, widthM]);

  const leftX = placement.speakerDistFromSideWallM;
  const rightX = widthM - placement.speakerDistFromSideWallM;
  const spkY = placement.speakerDistFromFrontWallM;
  const lisX = widthM / 2;
  const lisY = placement.listenerDistFromFrontWallM;

  const dims: PlanDimLine[] = [
    {
      id: 'listener-front',
      x1M: lisX,
      y1M: 0,
      x2M: lisX,
      y2M: lisY,
      label: `${formatLength(lisY, unit)} (38%)`,
      offsetPx: -22,
    },
    {
      id: 'spk-sep',
      x1M: leftX,
      y1M: spkY,
      x2M: rightX,
      y2M: spkY,
      label: formatLength(placement.speakerSeparationM, unit),
      offsetPx: -16,
    },
    {
      id: 'side',
      x1M: 0,
      y1M: spkY,
      x2M: leftX,
      y2M: spkY,
      label: formatLength(placement.speakerDistFromSideWallM, unit),
      offsetPx: 18,
    },
    {
      id: 'spk-front',
      x1M: leftX,
      y1M: 0,
      x2M: leftX,
      y2M: spkY,
      offsetPx: 18,
    },
    {
      id: 'triangle-L',
      x1M: leftX,
      y1M: spkY,
      x2M: lisX,
      y2M: lisY,
      offsetPx: 12,
    },
    {
      id: 'triangle-R',
      x1M: rightX,
      y1M: spkY,
      x2M: lisX,
      y2M: lisY,
      offsetPx: -12,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex justify-end">
        <UnitToggle unit={unit} onChange={setUnit} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 pb-6 border-b border-[var(--app-border)]">
        <ToolDimSlider
          label="Room length (L)"
          valueM={lengthM}
          minM={PLAN_BOUNDS.length.min}
          maxM={PLAN_BOUNDS.length.max}
          stepM={0.1}
          unit={unit}
          testId="dim-length"
          onChangeM={(v) => {
            const { room } = resizeRoom({ lengthM, widthM }, { lengthM: v }, {}, { bounds: PLAN_BOUNDS });
            setLengthM(room.lengthM);
          }}
        />
        <ToolDimSlider
          label="Room width (W)"
          valueM={widthM}
          minM={PLAN_BOUNDS.width.min}
          maxM={PLAN_BOUNDS.width.max}
          stepM={0.1}
          unit={unit}
          testId="dim-width"
          onChangeM={(v) => {
            const { room } = resizeRoom({ lengthM, widthM }, { widthM: v }, {}, { bounds: PLAN_BOUNDS });
            setWidthM(room.widthM);
          }}
        />
      </div>

      {/* Floorplan beside its metrics, matching first-reflection-calculator's
          composition — a lone narrow column left the right half of the desktop
          layout empty (S11, cohesion audit). */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <RoomPlan2D
          lengthM={lengthM}
          widthM={widthM}
          unit={unit}
          resizeWalls
          roomBounds={PLAN_BOUNDS}
          onRoomSizeChange={(l, w) => {
            const { room } = resizeRoom(
              { lengthM, widthM },
              { lengthM: l, widthM: w },
              {},
              { bounds: PLAN_BOUNDS },
            );
            setLengthM(room.lengthM);
            setWidthM(room.widthM);
          }}
          markers={[
            { id: 'L', xM: leftX, yM: spkY, label: 'L', color: 'var(--rt-accent)', draggable: false },
            { id: 'R', xM: rightX, yM: spkY, label: 'R', color: 'var(--rt-accent)', draggable: false },
            { id: 'lis', xM: lisX, yM: lisY, label: '●', color: 'var(--rt-accent)', draggable: false },
          ]}
          dims={dims}
          rays={[
            { id: 'tL', x1M: leftX, y1M: spkY, x2M: lisX, y2M: lisY, color: 'color-mix(in oklch, var(--rt-accent) 35%, transparent)' },
            { id: 'tR', x1M: rightX, y1M: spkY, x2M: lisX, y2M: lisY, color: 'color-mix(in oklch, var(--rt-accent) 35%, transparent)' },
            { id: 'sep', x1M: leftX, y1M: spkY, x2M: rightX, y2M: spkY, color: 'color-mix(in oklch, var(--rt-accent) 35%, transparent)' },
          ]}
          testId="placement-floorplan"
        />

        <div className="grid grid-cols-1 gap-4 content-start" data-testid="placement-metrics">
          <ToolMetricCard
            testId="metric-listener"
            label="Listener spot (38% rule)"
            value={formatLength(placement.listenerDistFromFrontWallM, unit)}
            hint="from front wall"
            emphasized
          />
          <ToolMetricCard
            testId="metric-separation"
            label="Speaker separation"
            value={formatLength(placement.speakerSeparationM, unit)}
            hint="center-to-center"
          />
          <ToolMetricCard
            testId="metric-side"
            label="Side wall spacing"
            value={formatLength(placement.speakerDistFromSideWallM, unit)}
            hint="from left/right walls"
          />
        </div>
      </div>

      <ToolLeadIn
        result={`Listening chair at ${formatLength(placement.listenerDistFromFrontWallM, unit)} from the front wall (38% rule), speakers ${formatLength(placement.speakerSeparationM, unit)} apart.`}
        ctaLabel="Open 3D speaker solver"
        seed={{ w: widthM, l: lengthM, h: 2.6, units: 'm' }}
      />
    </div>
  );
}
